import React, { useEffect, useState, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { toast } from 'sonner';
import { 
  Plus, Edit, Trash2, Save, X, Eye, EyeOff, 
  Package, Tag, Layers, ShoppingBag, Upload, Image,
  FileSpreadsheet, Download, AlertCircle, CheckCircle
} from 'lucide-react';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

const StoreManagement = ({ token }) => {
  const [activeSection, setActiveSection] = useState('products');
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  
  // Bulk Upload States
  const [showBulkUpload, setShowBulkUpload] = useState(false);
  const [bulkUploadPreview, setBulkUploadPreview] = useState(null);
  const [bulkUploading, setBulkUploading] = useState(false);
  const [bulkImporting, setBulkImporting] = useState(false);
  const bulkUploadRef = useRef(null);
  
  // Data
  const [categories, setCategories] = useState([]);
  const [subcategories, setSubcategories] = useState([]);
  const [products, setProducts] = useState([]);
  const [orders, setOrders] = useState([]);
  
  // Form states
  const [showCategoryForm, setShowCategoryForm] = useState(false);
  const [showSubcategoryForm, setShowSubcategoryForm] = useState(false);
  const [showProductForm, setShowProductForm] = useState(false);
  const [editingCategory, setEditingCategory] = useState(null);
  const [editingSubcategory, setEditingSubcategory] = useState(null);
  const [editingProduct, setEditingProduct] = useState(null);
  
  const productImageRef = useRef(null);
  
  // Form data
  const [categoryForm, setCategoryForm] = useState({ name: '', description: '', order: 0, is_active: true });
  const [subcategoryForm, setSubcategoryForm] = useState({ category_id: '', name: '', order: 0, is_active: true });
  const [productForm, setProductForm] = useState({
    name: '', category_id: '', subcategory_id: '', short_description: '', full_description: '',
    specifications: {}, price: '', discount_price: '', images: [], stock_quantity: 0, sku: '', is_active: true
  });
  const [specKey, setSpecKey] = useState('');
  const [specValue, setSpecValue] = useState('');

  const headers = { Authorization: `Bearer ${token}` };

  useEffect(() => {
    fetchStoreData();
  }, []);

  const fetchStoreData = async () => {
    setLoading(true);
    try {
      const [catRes, subRes, prodRes, orderRes] = await Promise.all([
        axios.get(`${API_URL}/store/categories/all`, { headers }),
        axios.get(`${API_URL}/store/subcategories/all`, { headers }),
        axios.get(`${API_URL}/store/products/all`, { headers }),
        axios.get(`${API_URL}/store/orders`, { headers }),
      ]);
      setCategories(catRes.data);
      setSubcategories(subRes.data);
      setProducts(prodRes.data);
      setOrders(orderRes.data);
    } catch (error) {
      console.error('Error fetching store data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleImageUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    setUploading(true);
    const formData = new FormData();
    formData.append('file', file);

    try {
      const response = await axios.post(`${API_URL}/upload`, formData, {
        headers: { ...headers, 'Content-Type': 'multipart/form-data' }
      });
      setProductForm(prev => ({ ...prev, images: [...prev.images, response.data.url] }));
      toast.success('Image uploaded!');
    } catch (error) {
      toast.error('Upload failed');
    } finally {
      setUploading(false);
    }
  };

  const removeImage = (index) => {
    setProductForm(prev => ({
      ...prev,
      images: prev.images.filter((_, i) => i !== index)
    }));
  };

  const addSpecification = () => {
    if (specKey && specValue) {
      setProductForm(prev => ({
        ...prev,
        specifications: { ...prev.specifications, [specKey]: specValue }
      }));
      setSpecKey('');
      setSpecValue('');
    }
  };

  const removeSpecification = (key) => {
    setProductForm(prev => {
      const newSpecs = { ...prev.specifications };
      delete newSpecs[key];
      return { ...prev, specifications: newSpecs };
    });
  };

  // Category CRUD
  const handleSaveCategory = async (e) => {
    e.preventDefault();
    const data = {
      ...categoryForm,
      slug: categoryForm.name.toLowerCase().replace(/\s+/g, '-').replace(/[^a-z0-9-]/g, '')
    };
    try {
      if (editingCategory) {
        await axios.put(`${API_URL}/store/categories/${editingCategory.id}`, data, { headers });
        toast.success('Category updated!');
      } else {
        await axios.post(`${API_URL}/store/categories`, data, { headers });
        toast.success('Category created!');
      }
      setShowCategoryForm(false);
      setEditingCategory(null);
      setCategoryForm({ name: '', description: '', order: 0, is_active: true });
      fetchStoreData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save category');
    }
  };

  const handleDeleteCategory = async (id) => {
    if (!window.confirm('Delete this category and all its subcategories?')) return;
    try {
      await axios.delete(`${API_URL}/store/categories/${id}`, { headers });
      toast.success('Category deleted!');
      fetchStoreData();
    } catch (error) {
      toast.error('Failed to delete category');
    }
  };

  // Subcategory CRUD
  const handleSaveSubcategory = async (e) => {
    e.preventDefault();
    const data = {
      ...subcategoryForm,
      slug: subcategoryForm.name.toLowerCase().replace(/\s+/g, '-').replace(/[^a-z0-9-]/g, '')
    };
    try {
      if (editingSubcategory) {
        await axios.put(`${API_URL}/store/subcategories/${editingSubcategory.id}`, data, { headers });
        toast.success('Subcategory updated!');
      } else {
        await axios.post(`${API_URL}/store/subcategories`, data, { headers });
        toast.success('Subcategory created!');
      }
      setShowSubcategoryForm(false);
      setEditingSubcategory(null);
      setSubcategoryForm({ category_id: '', name: '', order: 0, is_active: true });
      fetchStoreData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save subcategory');
    }
  };

  const handleDeleteSubcategory = async (id) => {
    if (!window.confirm('Delete this subcategory?')) return;
    try {
      await axios.delete(`${API_URL}/store/subcategories/${id}`, { headers });
      toast.success('Subcategory deleted!');
      fetchStoreData();
    } catch (error) {
      toast.error('Failed to delete subcategory');
    }
  };

  // Product CRUD
  const handleSaveProduct = async (e) => {
    e.preventDefault();
    const data = {
      ...productForm,
      slug: productForm.name.toLowerCase().replace(/\s+/g, '-').replace(/[^a-z0-9-]/g, ''),
      price: parseFloat(productForm.price),
      discount_price: productForm.discount_price ? parseFloat(productForm.discount_price) : null,
      stock_quantity: parseInt(productForm.stock_quantity)
    };
    try {
      if (editingProduct) {
        await axios.put(`${API_URL}/store/products/${editingProduct.id}`, data, { headers });
        toast.success('Product updated!');
      } else {
        await axios.post(`${API_URL}/store/products`, data, { headers });
        toast.success('Product created!');
      }
      setShowProductForm(false);
      setEditingProduct(null);
      setProductForm({
        name: '', category_id: '', subcategory_id: '', short_description: '', full_description: '',
        specifications: {}, price: '', discount_price: '', images: [], stock_quantity: 0, sku: '', is_active: true
      });
      fetchStoreData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save product');
    }
  };

  const handleDeleteProduct = async (id) => {
    if (!window.confirm('Delete this product?')) return;
    try {
      await axios.delete(`${API_URL}/store/products/${id}`, { headers });
      toast.success('Product deleted!');
      fetchStoreData();
    } catch (error) {
      toast.error('Failed to delete product');
    }
  };

  const handleToggleProduct = async (product) => {
    try {
      await axios.put(`${API_URL}/store/products/${product.id}`, 
        { ...product, is_active: !product.is_active }, { headers });
      toast.success(`Product ${product.is_active ? 'deactivated' : 'activated'}!`);
      fetchStoreData();
    } catch (error) {
      toast.error('Failed to update product');
    }
  };

  // Bulk Upload Functions
  const handleDownloadTemplate = async () => {
    try {
      const response = await axios.get(`${API_URL}/store/products/template/download`, {
        headers,
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'product_upload_template.xlsx');
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success('Template downloaded!');
    } catch (error) {
      toast.error('Failed to download template');
    }
  };

  const handleBulkFileSelect = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    setBulkUploading(true);
    const formData = new FormData();
    formData.append('file', file);

    try {
      const response = await axios.post(`${API_URL}/store/products/bulk-upload/preview`, formData, {
        headers: { ...headers, 'Content-Type': 'multipart/form-data' }
      });
      setBulkUploadPreview(response.data);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to process file');
      setBulkUploadPreview(null);
    } finally {
      setBulkUploading(false);
    }
  };

  const handleBulkImport = async () => {
    const file = bulkUploadRef.current?.files[0];
    if (!file) return;

    setBulkImporting(true);
    const formData = new FormData();
    formData.append('file', file);

    try {
      const response = await axios.post(`${API_URL}/store/products/bulk-upload/import`, formData, {
        headers: { ...headers, 'Content-Type': 'multipart/form-data' }
      });
      toast.success(response.data.message);
      setShowBulkUpload(false);
      setBulkUploadPreview(null);
      fetchStoreData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Import failed');
    } finally {
      setBulkImporting(false);
    }
  };

  // Order status update
  const handleUpdateOrderStatus = async (orderId, status) => {
    try {
      await axios.put(`${API_URL}/store/orders/${orderId}/status?status=${status}`, {}, { headers });
      toast.success('Order status updated!');
      fetchStoreData();
    } catch (error) {
      toast.error('Failed to update order status');
    }
  };

  const getCategoryName = (id) => categories.find(c => c.id === id)?.name || 'Unknown';
  const getSubcategoryName = (id) => subcategories.find(s => s.id === id)?.name || '';
  const filteredSubcategories = subcategories.filter(s => s.category_id === productForm.category_id);

  const tabs = [
    { id: 'products', label: 'Products', icon: Package, count: products.length },
    { id: 'categories', label: 'Categories', icon: Layers, count: categories.length },
    { id: 'subcategories', label: 'Subcategories', icon: Tag, count: subcategories.length },
    { id: 'orders', label: 'Orders', icon: ShoppingBag, count: orders.length },
  ];

  return (
    <div className="space-y-6">
      {/* Section Tabs */}
      <div className="flex flex-wrap gap-2 mb-6">
        {tabs.map(tab => (
          <button
            key={tab.id}
            onClick={() => setActiveSection(tab.id)}
            className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-colors ${
              activeSection === tab.id ? 'bg-primary text-dark' : 'bg-white/5 text-gray-400 hover:bg-white/10'
            }`}
          >
            <tab.icon size={18} />
            {tab.label}
            <span className="px-2 py-0.5 bg-black/20 rounded text-xs">{tab.count}</span>
          </button>
        ))}
      </div>

      {/* Categories Section */}
      {activeSection === 'categories' && (
        <div className="space-y-4">
          <div className="flex justify-between items-center">
            <h3 className="text-xl font-semibold text-white">Categories</h3>
            <Button onClick={() => { setShowCategoryForm(true); setEditingCategory(null); setCategoryForm({ name: '', description: '', order: 0, is_active: true }); }}>
              <Plus size={18} className="mr-2" /> Add Category
            </Button>
          </div>

          {showCategoryForm && (
            <motion.form initial={{ opacity: 0, y: -10 }} animate={{ opacity: 1, y: 0 }} onSubmit={handleSaveCategory} className="glass rounded-xl p-6 space-y-4">
              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <label className="text-xs font-bold uppercase text-primary mb-1 block">Name *</label>
                  <Input value={categoryForm.name} onChange={(e) => setCategoryForm({ ...categoryForm, name: e.target.value })} required className="bg-white/5" />
                </div>
                <div>
                  <label className="text-xs font-bold uppercase text-primary mb-1 block">Order</label>
                  <Input type="number" value={categoryForm.order} onChange={(e) => setCategoryForm({ ...categoryForm, order: parseInt(e.target.value) })} className="bg-white/5" />
                </div>
              </div>
              <div>
                <label className="text-xs font-bold uppercase text-primary mb-1 block">Description</label>
                <Textarea value={categoryForm.description} onChange={(e) => setCategoryForm({ ...categoryForm, description: e.target.value })} className="bg-white/5" />
              </div>
              <div className="flex items-center gap-2">
                <input type="checkbox" checked={categoryForm.is_active} onChange={(e) => setCategoryForm({ ...categoryForm, is_active: e.target.checked })} />
                <label className="text-sm text-gray-400">Active</label>
              </div>
              <div className="flex gap-2">
                <Button type="submit"><Save size={18} className="mr-2" /> Save</Button>
                <Button type="button" variant="outline" onClick={() => setShowCategoryForm(false)}><X size={18} className="mr-2" /> Cancel</Button>
              </div>
            </motion.form>
          )}

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
            {categories.map(cat => (
              <div key={cat.id} className="glass rounded-xl p-4">
                <div className="flex justify-between items-start mb-2">
                  <h4 className="font-semibold text-white">{cat.name}</h4>
                  <span className={`px-2 py-0.5 text-xs rounded ${cat.is_active ? 'bg-green-500/20 text-green-400' : 'bg-red-500/20 text-red-400'}`}>
                    {cat.is_active ? 'Active' : 'Inactive'}
                  </span>
                </div>
                <p className="text-sm text-gray-400 mb-3">{cat.description || 'No description'}</p>
                <div className="flex gap-2">
                  <button onClick={() => { setEditingCategory(cat); setCategoryForm(cat); setShowCategoryForm(true); }} className="p-2 bg-blue-500/20 text-blue-400 rounded-lg hover:bg-blue-500/30">
                    <Edit size={16} />
                  </button>
                  <button onClick={() => handleDeleteCategory(cat.id)} className="p-2 bg-red-500/20 text-red-400 rounded-lg hover:bg-red-500/30">
                    <Trash2 size={16} />
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Subcategories Section */}
      {activeSection === 'subcategories' && (
        <div className="space-y-4">
          <div className="flex justify-between items-center">
            <h3 className="text-xl font-semibold text-white">Subcategories</h3>
            <Button onClick={() => { setShowSubcategoryForm(true); setEditingSubcategory(null); setSubcategoryForm({ category_id: categories[0]?.id || '', name: '', order: 0, is_active: true }); }}>
              <Plus size={18} className="mr-2" /> Add Subcategory
            </Button>
          </div>

          {showSubcategoryForm && (
            <motion.form initial={{ opacity: 0, y: -10 }} animate={{ opacity: 1, y: 0 }} onSubmit={handleSaveSubcategory} className="glass rounded-xl p-6 space-y-4">
              <div className="grid md:grid-cols-3 gap-4">
                <div>
                  <label className="text-xs font-bold uppercase text-primary mb-1 block">Category *</label>
                  <select value={subcategoryForm.category_id} onChange={(e) => setSubcategoryForm({ ...subcategoryForm, category_id: e.target.value })} required className="w-full px-4 py-2 bg-[#1a1a2e] border border-white/20 rounded-lg text-white [&>option]:bg-[#1a1a2e] [&>option]:text-white">
                    <option value="">Select Category</option>
                    {categories.map(cat => <option key={cat.id} value={cat.id}>{cat.name}</option>)}
                  </select>
                </div>
                <div>
                  <label className="text-xs font-bold uppercase text-primary mb-1 block">Name *</label>
                  <Input value={subcategoryForm.name} onChange={(e) => setSubcategoryForm({ ...subcategoryForm, name: e.target.value })} required className="bg-white/5" />
                </div>
                <div>
                  <label className="text-xs font-bold uppercase text-primary mb-1 block">Order</label>
                  <Input type="number" value={subcategoryForm.order} onChange={(e) => setSubcategoryForm({ ...subcategoryForm, order: parseInt(e.target.value) })} className="bg-white/5" />
                </div>
              </div>
              <div className="flex items-center gap-2">
                <input type="checkbox" checked={subcategoryForm.is_active} onChange={(e) => setSubcategoryForm({ ...subcategoryForm, is_active: e.target.checked })} />
                <label className="text-sm text-gray-400">Active</label>
              </div>
              <div className="flex gap-2">
                <Button type="submit"><Save size={18} className="mr-2" /> Save</Button>
                <Button type="button" variant="outline" onClick={() => setShowSubcategoryForm(false)}><X size={18} className="mr-2" /> Cancel</Button>
              </div>
            </motion.form>
          )}

          <div className="space-y-2">
            {categories.map(cat => {
              const subs = subcategories.filter(s => s.category_id === cat.id);
              if (subs.length === 0) return null;
              return (
                <div key={cat.id} className="glass rounded-xl p-4">
                  <h4 className="font-semibold text-primary mb-3">{cat.name}</h4>
                  <div className="flex flex-wrap gap-2">
                    {subs.map(sub => (
                      <div key={sub.id} className="flex items-center gap-2 px-3 py-2 bg-white/5 rounded-lg">
                        <span className={sub.is_active ? 'text-white' : 'text-gray-500'}>{sub.name}</span>
                        <button onClick={() => { setEditingSubcategory(sub); setSubcategoryForm(sub); setShowSubcategoryForm(true); }} className="text-blue-400 hover:text-blue-300">
                          <Edit size={14} />
                        </button>
                        <button onClick={() => handleDeleteSubcategory(sub.id)} className="text-red-400 hover:text-red-300">
                          <Trash2 size={14} />
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Products Section */}
      {activeSection === 'products' && (
        <div className="space-y-4">
          <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
            <h3 className="text-xl font-semibold text-white">Products ({products.length})</h3>
            <div className="flex flex-wrap gap-2">
              <Button variant="outline" onClick={() => setShowBulkUpload(true)}>
                <FileSpreadsheet size={18} className="mr-2" /> Bulk Upload
              </Button>
              <Button onClick={() => { setShowProductForm(true); setEditingProduct(null); setProductForm({ name: '', category_id: '', subcategory_id: '', short_description: '', full_description: '', specifications: {}, price: '', discount_price: '', images: [], stock_quantity: 0, sku: '', is_active: true }); }}>
                <Plus size={18} className="mr-2" /> Add Product
              </Button>
            </div>
          </div>

          {/* Bulk Upload Modal */}
          <AnimatePresence>
            {showBulkUpload && (
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                className="fixed inset-0 bg-black/70 backdrop-blur-sm z-50 flex items-center justify-center p-4"
                onClick={() => { setShowBulkUpload(false); setBulkUploadPreview(null); }}
              >
                <motion.div
                  initial={{ scale: 0.9, opacity: 0 }}
                  animate={{ scale: 1, opacity: 1 }}
                  exit={{ scale: 0.9, opacity: 0 }}
                  className="bg-[#1a1a2e] rounded-2xl p-6 w-full max-w-2xl max-h-[80vh] overflow-y-auto"
                  onClick={(e) => e.stopPropagation()}
                >
                  <div className="flex justify-between items-center mb-6">
                    <h3 className="text-xl font-bold text-white flex items-center gap-2">
                      <FileSpreadsheet className="text-primary" />
                      Bulk Product Upload
                    </h3>
                    <button onClick={() => { setShowBulkUpload(false); setBulkUploadPreview(null); }} className="text-gray-400 hover:text-white">
                      <X size={24} />
                    </button>
                  </div>

                  {/* Step 1: Download Template */}
                  <div className="mb-6 p-4 bg-white/5 rounded-xl border border-white/10">
                    <h4 className="font-semibold text-white mb-2">Step 1: Download Template</h4>
                    <p className="text-sm text-gray-400 mb-3">
                      Download the Excel template with the correct format for bulk uploading products.
                    </p>
                    <Button variant="outline" onClick={handleDownloadTemplate}>
                      <Download size={18} className="mr-2" /> Download Template (.xlsx)
                    </Button>
                  </div>

                  {/* Step 2: Upload File */}
                  <div className="mb-6 p-4 bg-white/5 rounded-xl border border-white/10">
                    <h4 className="font-semibold text-white mb-2">Step 2: Upload Your File</h4>
                    <p className="text-sm text-gray-400 mb-3">
                      Upload your filled Excel (.xlsx) or CSV file to preview and import products.
                    </p>
                    <input
                      type="file"
                      ref={bulkUploadRef}
                      onChange={handleBulkFileSelect}
                      accept=".xlsx,.csv"
                      className="hidden"
                    />
                    <Button
                      variant="outline"
                      onClick={() => bulkUploadRef.current?.click()}
                      disabled={bulkUploading}
                    >
                      {bulkUploading ? (
                        <>
                          <span className="animate-spin mr-2">⏳</span> Processing...
                        </>
                      ) : (
                        <>
                          <Upload size={18} className="mr-2" /> Select File
                        </>
                      )}
                    </Button>
                  </div>

                  {/* Step 3: Preview & Import */}
                  {bulkUploadPreview && (
                    <div className="p-4 bg-white/5 rounded-xl border border-white/10">
                      <h4 className="font-semibold text-white mb-4">Step 3: Preview & Import</h4>
                      
                      {/* Summary Stats */}
                      <div className="grid grid-cols-3 gap-4 mb-4">
                        <div className="bg-white/5 rounded-lg p-3 text-center">
                          <p className="text-2xl font-bold text-white">{bulkUploadPreview.total_rows}</p>
                          <p className="text-xs text-gray-400">Total Rows</p>
                        </div>
                        <div className="bg-green-500/10 rounded-lg p-3 text-center">
                          <p className="text-2xl font-bold text-green-400">{bulkUploadPreview.valid_rows}</p>
                          <p className="text-xs text-gray-400">Valid</p>
                        </div>
                        <div className="bg-red-500/10 rounded-lg p-3 text-center">
                          <p className="text-2xl font-bold text-red-400">{bulkUploadPreview.invalid_rows}</p>
                          <p className="text-xs text-gray-400">Invalid</p>
                        </div>
                      </div>

                      {/* Errors */}
                      {bulkUploadPreview.errors?.length > 0 && (
                        <div className="mb-4 p-3 bg-red-500/10 rounded-lg border border-red-500/20">
                          <h5 className="text-sm font-semibold text-red-400 mb-2 flex items-center gap-1">
                            <AlertCircle size={16} /> Validation Errors
                          </h5>
                          <ul className="text-xs text-red-300 space-y-1 max-h-32 overflow-y-auto">
                            {bulkUploadPreview.errors.map((err, idx) => (
                              <li key={idx}>{err}</li>
                            ))}
                          </ul>
                        </div>
                      )}

                      {/* Preview Table */}
                      <div className="mb-4 overflow-x-auto">
                        <table className="w-full text-sm">
                          <thead>
                            <tr className="border-b border-white/10">
                              <th className="text-left py-2 px-2 text-gray-400">Status</th>
                              <th className="text-left py-2 px-2 text-gray-400">Name</th>
                              <th className="text-left py-2 px-2 text-gray-400">Category</th>
                              <th className="text-left py-2 px-2 text-gray-400">Price</th>
                              <th className="text-left py-2 px-2 text-gray-400">SKU</th>
                            </tr>
                          </thead>
                          <tbody>
                            {bulkUploadPreview.preview?.slice(0, 5).map((item, idx) => (
                              <tr key={idx} className="border-b border-white/5">
                                <td className="py-2 px-2">
                                  {item.is_valid ? (
                                    <CheckCircle size={16} className="text-green-400" />
                                  ) : (
                                    <AlertCircle size={16} className="text-red-400" />
                                  )}
                                </td>
                                <td className="py-2 px-2 text-white">{item.name}</td>
                                <td className="py-2 px-2 text-gray-400">{item.category_name}</td>
                                <td className="py-2 px-2 text-primary">₹{item.price}</td>
                                <td className="py-2 px-2 text-gray-400">{item.sku}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                        {bulkUploadPreview.preview?.length > 5 && (
                          <p className="text-xs text-gray-500 mt-2">
                            ... and {bulkUploadPreview.preview.length - 5} more rows
                          </p>
                        )}
                      </div>

                      {/* Import Button */}
                      <Button
                        onClick={handleBulkImport}
                        disabled={!bulkUploadPreview.can_import || bulkImporting}
                        className="w-full"
                      >
                        {bulkImporting ? (
                          <>
                            <span className="animate-spin mr-2">⏳</span> Importing...
                          </>
                        ) : (
                          <>
                            <CheckCircle size={18} className="mr-2" />
                            Import {bulkUploadPreview.valid_rows} Products
                          </>
                        )}
                      </Button>
                    </div>
                  )}
                </motion.div>
              </motion.div>
            )}
          </AnimatePresence>

          {showProductForm && (
            <motion.form initial={{ opacity: 0, y: -10 }} animate={{ opacity: 1, y: 0 }} onSubmit={handleSaveProduct} className="glass rounded-xl p-6 space-y-4">
              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <label className="text-xs font-bold uppercase text-primary mb-1 block">Product Name *</label>
                  <Input value={productForm.name} onChange={(e) => setProductForm({ ...productForm, name: e.target.value })} required className="bg-white/5" />
                </div>
                <div>
                  <label className="text-xs font-bold uppercase text-primary mb-1 block">SKU *</label>
                  <Input value={productForm.sku} onChange={(e) => setProductForm({ ...productForm, sku: e.target.value })} required className="bg-white/5" />
                </div>
              </div>
              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <label className="text-xs font-bold uppercase text-primary mb-1 block">Category *</label>
                  <select value={productForm.category_id} onChange={(e) => setProductForm({ ...productForm, category_id: e.target.value, subcategory_id: '' })} required className="w-full px-4 py-2 bg-[#1a1a2e] border border-white/20 rounded-lg text-white [&>option]:bg-[#1a1a2e] [&>option]:text-white">
                    <option value="">Select Category</option>
                    {categories.map(cat => <option key={cat.id} value={cat.id}>{cat.name}</option>)}
                  </select>
                </div>
                <div>
                  <label className="text-xs font-bold uppercase text-primary mb-1 block">Subcategory</label>
                  <select value={productForm.subcategory_id} onChange={(e) => setProductForm({ ...productForm, subcategory_id: e.target.value })} className="w-full px-4 py-2 bg-[#1a1a2e] border border-white/20 rounded-lg text-white [&>option]:bg-[#1a1a2e] [&>option]:text-white">
                    <option value="">Select Subcategory</option>
                    {filteredSubcategories.map(sub => <option key={sub.id} value={sub.id}>{sub.name}</option>)}
                  </select>
                </div>
              </div>
              <div className="grid md:grid-cols-3 gap-4">
                <div>
                  <label className="text-xs font-bold uppercase text-primary mb-1 block">Price (₹) *</label>
                  <Input type="number" step="0.01" value={productForm.price} onChange={(e) => setProductForm({ ...productForm, price: e.target.value })} required className="bg-white/5" />
                </div>
                <div>
                  <label className="text-xs font-bold uppercase text-primary mb-1 block">Discount Price (₹)</label>
                  <Input type="number" step="0.01" value={productForm.discount_price} onChange={(e) => setProductForm({ ...productForm, discount_price: e.target.value })} className="bg-white/5" />
                </div>
                <div>
                  <label className="text-xs font-bold uppercase text-primary mb-1 block">Stock Quantity *</label>
                  <Input type="number" value={productForm.stock_quantity} onChange={(e) => setProductForm({ ...productForm, stock_quantity: e.target.value })} required className="bg-white/5" />
                </div>
              </div>
              <div>
                <label className="text-xs font-bold uppercase text-primary mb-1 block">Short Description *</label>
                <Textarea value={productForm.short_description} onChange={(e) => setProductForm({ ...productForm, short_description: e.target.value })} required className="bg-white/5" rows={2} />
              </div>
              <div>
                <label className="text-xs font-bold uppercase text-primary mb-1 block">Full Description</label>
                <Textarea value={productForm.full_description} onChange={(e) => setProductForm({ ...productForm, full_description: e.target.value })} className="bg-white/5" rows={4} />
              </div>
              
              {/* Specifications */}
              <div>
                <label className="text-xs font-bold uppercase text-primary mb-1 block">Specifications</label>
                <div className="flex gap-2 mb-2">
                  <Input placeholder="Key (e.g. Weight)" value={specKey} onChange={(e) => setSpecKey(e.target.value)} className="bg-white/5 flex-1" />
                  <Input placeholder="Value (e.g. 500g)" value={specValue} onChange={(e) => setSpecValue(e.target.value)} className="bg-white/5 flex-1" />
                  <Button type="button" onClick={addSpecification}><Plus size={18} /></Button>
                </div>
                <div className="flex flex-wrap gap-2">
                  {Object.entries(productForm.specifications).map(([key, value]) => (
                    <span key={key} className="flex items-center gap-1 px-3 py-1 bg-white/10 rounded-lg text-sm">
                      <strong>{key}:</strong> {value}
                      <button type="button" onClick={() => removeSpecification(key)} className="ml-1 text-red-400"><X size={14} /></button>
                    </span>
                  ))}
                </div>
              </div>

              {/* Images */}
              <div>
                <label className="text-xs font-bold uppercase text-primary mb-1 block">Product Images</label>
                <input type="file" ref={productImageRef} onChange={handleImageUpload} accept="image/*" className="hidden" />
                <div className="flex gap-2 flex-wrap">
                  {productForm.images.map((img, idx) => (
                    <div key={idx} className="relative w-20 h-20">
                      <img src={img} alt={`Product ${idx}`} className="w-full h-full object-cover rounded-lg" />
                      <button type="button" onClick={() => removeImage(idx)} className="absolute -top-2 -right-2 w-5 h-5 bg-red-500 rounded-full flex items-center justify-center"><X size={12} /></button>
                    </div>
                  ))}
                  <button type="button" onClick={() => productImageRef.current?.click()} disabled={uploading} className="w-20 h-20 border-2 border-dashed border-white/20 rounded-lg flex items-center justify-center hover:border-primary transition-colors">
                    {uploading ? <span className="animate-spin">⏳</span> : <Upload size={24} className="text-gray-400" />}
                  </button>
                </div>
              </div>

              <div className="flex items-center gap-2">
                <input type="checkbox" checked={productForm.is_active} onChange={(e) => setProductForm({ ...productForm, is_active: e.target.checked })} />
                <label className="text-sm text-gray-400">Active (visible in store)</label>
              </div>
              <div className="flex gap-2">
                <Button type="submit"><Save size={18} className="mr-2" /> {editingProduct ? 'Update' : 'Create'} Product</Button>
                <Button type="button" variant="outline" onClick={() => setShowProductForm(false)}><X size={18} className="mr-2" /> Cancel</Button>
              </div>
            </motion.form>
          )}

          {/* Products List */}
          <div className="space-y-3">
            {products.map(product => (
              <div key={product.id} className="glass rounded-xl p-4 flex flex-col md:flex-row gap-4">
                <div className="w-full md:w-24 h-24 flex-shrink-0 rounded-lg overflow-hidden bg-white/5">
                  {product.images?.[0] ? <img src={product.images[0]} alt={product.name} className="w-full h-full object-cover" /> : <Package className="w-full h-full p-4 text-gray-600" />}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1">
                    <span className={`px-2 py-0.5 text-xs rounded ${product.is_active ? 'bg-green-500/20 text-green-400' : 'bg-red-500/20 text-red-400'}`}>
                      {product.is_active ? 'Active' : 'Inactive'}
                    </span>
                    <span className="text-xs text-gray-500">SKU: {product.sku}</span>
                  </div>
                  <h4 className="font-semibold text-white mb-1 line-clamp-1">{product.name}</h4>
                  <p className="text-sm text-gray-400 mb-2">{getCategoryName(product.category_id)} {getSubcategoryName(product.subcategory_id) && `> ${getSubcategoryName(product.subcategory_id)}`}</p>
                  <div className="flex items-center gap-4">
                    <span className="text-primary font-bold">₹{product.discount_price || product.price}</span>
                    {product.discount_price && <span className="text-sm text-gray-500 line-through">₹{product.price}</span>}
                    <span className="text-sm text-gray-400">Stock: {product.stock_quantity}</span>
                  </div>
                </div>
                <div className="flex md:flex-col gap-2">
                  <button onClick={() => handleToggleProduct(product)} className={`p-2 rounded-lg ${product.is_active ? 'bg-green-500/20 text-green-400' : 'bg-yellow-500/20 text-yellow-400'}`}>
                    {product.is_active ? <Eye size={18} /> : <EyeOff size={18} />}
                  </button>
                  <button onClick={() => { setEditingProduct(product); setProductForm({ ...product, price: product.price.toString(), discount_price: product.discount_price?.toString() || '', stock_quantity: product.stock_quantity.toString() }); setShowProductForm(true); }} className="p-2 bg-blue-500/20 text-blue-400 rounded-lg hover:bg-blue-500/30">
                    <Edit size={18} />
                  </button>
                  <button onClick={() => handleDeleteProduct(product.id)} className="p-2 bg-red-500/20 text-red-400 rounded-lg hover:bg-red-500/30">
                    <Trash2 size={18} />
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Orders Section */}
      {activeSection === 'orders' && (
        <div className="space-y-4">
          <h3 className="text-xl font-semibold text-white">Orders ({orders.length})</h3>
          
          {orders.length === 0 ? (
            <div className="glass rounded-xl p-8 text-center text-gray-400">No orders yet</div>
          ) : (
            <div className="space-y-4">
              {orders.map(order => (
                <div key={order.id} className="glass rounded-xl p-4">
                  <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-4">
                    <div>
                      <div className="flex items-center gap-2 mb-1">
                        <span className="font-semibold text-primary">{order.order_number}</span>
                        <span className={`px-2 py-0.5 text-xs rounded ${
                          order.status === 'completed' ? 'bg-green-500/20 text-green-400' :
                          order.status === 'processing' ? 'bg-blue-500/20 text-blue-400' :
                          order.status === 'cancelled' ? 'bg-red-500/20 text-red-400' :
                          'bg-yellow-500/20 text-yellow-400'
                        }`}>
                          {order.status.charAt(0).toUpperCase() + order.status.slice(1)}
                        </span>
                        <span className={`px-2 py-0.5 text-xs rounded ${order.payment_status === 'paid' ? 'bg-green-500/20 text-green-400' : 'bg-yellow-500/20 text-yellow-400'}`}>
                          {order.payment_status === 'paid' ? 'Paid' : 'Pending Payment'}
                        </span>
                      </div>
                      <p className="text-sm text-gray-400">{order.customer_name} • {order.phone} • {order.email}</p>
                      <p className="text-sm text-gray-500">{order.address}, {order.city}, {order.state} - {order.pincode}</p>
                    </div>
                    <div className="text-right">
                      <p className="text-xl font-bold text-primary">₹{order.total.toLocaleString()}</p>
                      <p className="text-xs text-gray-500">{new Date(order.created_at).toLocaleDateString()}</p>
                    </div>
                  </div>
                  
                  <div className="border-t border-white/10 pt-3 mb-3">
                    <p className="text-sm text-gray-400 mb-2">Items:</p>
                    <div className="flex flex-wrap gap-2">
                      {order.items.map((item, idx) => (
                        <span key={idx} className="px-3 py-1 bg-white/5 rounded text-sm">
                          {item.name} x{item.quantity} = ₹{item.subtotal}
                        </span>
                      ))}
                    </div>
                  </div>
                  
                  <div className="flex gap-2">
                    <select
                      value={order.status}
                      onChange={(e) => handleUpdateOrderStatus(order.id, e.target.value)}
                      className="px-3 py-2 bg-[#1a1a2e] border border-white/20 rounded-lg text-white text-sm [&>option]:bg-[#1a1a2e] [&>option]:text-white"
                    >
                      <option value="pending">Pending</option>
                      <option value="processing">Processing</option>
                      <option value="completed">Completed</option>
                      <option value="cancelled">Cancelled</option>
                    </select>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default StoreManagement;
